<template>
  <div ref="containerRef" class="relative">
    <AppLabel v-if="label" :for="id"
      >{{ label }} <AppRequiredLabel v-if="required"
    /></AppLabel>
    <div
      ref="comboboxRef"
      :class="[
        'min-h-[42px] border border-0.5 rounded-md px-3 py-2 inline-flex w-full',
        error
          ? 'border-red-2 dark:border-dark-red focus-within:ring-red-2'
          : 'border-grey-11 dark:border-dark-border focus-within:ring-blue-1',
        'focus-within:border-black dark:focus-within:border-dark-ink',
        disabled ? 'cursor-not-allowed bg-grey-9 dark:bg-dark-surface-2' : '',
        `bg-${background}`,
        darkControlBgClass(background),
      ]"
      role="combobox"
      :aria-expanded="showSuggestions"
      :aria-controls="`${id}-listbox`"
      aria-haspopup="listbox"
      :aria-owns="showSuggestions ? `${id}-listbox` : undefined"
    >
      <div
        :class="
          multiple ? 'flex flex-wrap items-center w-full gap-2' : 'contents'
        "
      >
        <ul
          v-if="hasSelectedValues"
          role="list"
          class="flex flex-wrap items-center gap-2"
        >
          <li
            v-for="item in selectedItems"
            :key="item[valueKey]"
            class="rounded-full px-2 py-1 text-sm flex items-center"
            :class="[
              disabled
                ? 'bg-grey-13 dark:bg-dark-border cursor-not-allowed'
                : 'bg-grey-9 dark:bg-dark-surface-2',
            ]"
            role="listitem"
          >
            {{ item[labelKey] }}
            <button
              :disabled="disabled"
              :class="[disabled && 'cursor-not-allowed', 'ml-2']"
              :aria-label="`Remove ${item[labelKey]}`"
              @click="removeItem(item)"
            >
              <mdicon name="close" :size="16" aria-hidden="true" />
            </button>
          </li>
        </ul>
        <div
          :class="
            multiple
              ? 'flex items-center gap-2 flex-1 min-w-[200px]'
              : 'contents'
          "
        >
          <mdicon
            v-if="!hasSelectedValues"
            name="magnify"
            aria-hidden="true"
            :class="[
              'text-grey-5 dark:text-dark-ink-muted',
              multiple ? 'shrink-0' : 'pr-2 content-center',
            ]"
            :size="20"
          />
          <input
            :id="id"
            ref="inputRef"
            v-model="searchTerm"
            :placeholder="inputPlaceholder"
            :disabled="disabled"
            :required="required"
            :class="[
              'w-full bg-transparent text-sm focus:outline-hidden',
              disabled && 'cursor-not-allowed',
            ]"
            :aria-label="label || 'Search'"
            :aria-invalid="!!error"
            :aria-describedby="
              error ? `${id}-error` : helpLabel ? `${id}-help` : undefined
            "
            :aria-controls="showSuggestions ? `${id}-listbox` : undefined"
            role="searchbox"
            autocomplete="off"
            @input="onInput"
            @focus="onInput"
            @keydown.esc.prevent="closeSuggestions"
          />
          <div class="flex items-center" @click="toggleSuggestionsAndFocus">
            <slot name="label" />
            <AppAutocompleteLabel
              v-if="!multiple && modelValue?.category"
              class="mr-1"
              :background="modelValue.meta?.background"
              :text="modelValue.meta?.text"
            >
              {{ modelValue.category }}
            </AppAutocompleteLabel>
            <mdicon
              v-if="error"
              name="alert-circle-outline"
              class="text-red-1 dark:text-dark-red pr-2"
              aria-hidden="true"
            />
            <mdicon
              :name="chevronIcon"
              :size="20"
              :aria-label="
                showSuggestions ? 'Collapse suggestions' : 'Expand suggestions'
              "
            />
          </div>
        </div>
      </div>
    </div>
    <Teleport to="body">
      <ul
        v-if="showSuggestions"
        :id="`${id}-listbox`"
        ref="panelRef"
        :style="panelStyle"
        role="listbox"
        class="z-50 bg-white dark:bg-dark-surface shadow-lg rounded-md text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm"
        :aria-label="label ? `${label} suggestions` : 'Search suggestions'"
      >
        <li
          v-if="isLoading"
          class="py-3 px-4 text-black dark:text-dark-ink flex items-center"
          role="status"
        >
          <mdicon name="loading" class="animate-spin mr-2" aria-hidden="true" />
          Loading
        </li>
        <template v-else>
          <li
            v-if="apiError"
            class="py-3 px-4 text-red-1 dark:text-dark-red"
            role="alert"
          >
            {{ apiError }}
          </li>
          <li
            v-else-if="!visibleSuggestions.length"
            class="py-3 px-4 text-black dark:text-dark-ink"
            role="alert"
          >
            No Results
          </li>
          <li
            v-for="suggestion in visibleSuggestions"
            :key="suggestion[valueKey]"
            :class="[
              !$slots.suggestion &&
                'cursor-pointer items-center flex select-none relative py-3 pl-3 pr-9 hover:bg-grey-9 dark:hover:bg-dark-surface-2 focus:bg-grey-9 dark:focus:bg-dark-surface-2 focus:outline-hidden',
              !$slots.suggestion && suggestionSelected(suggestion)
                ? 'bg-grey-10 dark:bg-dark-surface-2'
                : '',
            ]"
            role="option"
            :aria-selected="suggestionSelected(suggestion)"
            tabindex="0"
            @click="selectSuggestion(suggestion)"
            @keydown.enter.prevent="selectSuggestion(suggestion)"
            @keydown.space.prevent="selectSuggestion(suggestion)"
          >
            <slot
              name="suggestion"
              :suggestion="suggestion"
              :search-term="searchTerm"
            >
              <span class="justify-start inline-flex flex-1 flex-col">
                {{ suggestion[labelKey] }}
                <span
                  v-if="suggestion.description"
                  class="block text-grey-11 dark:text-dark-ink-muted"
                  >{{ suggestion.description }}</span
                >
              </span>
              <span class="justify-end inline-flex items-center">
                <div v-if="suggestion?.category" role="list">
                  <AppAutocompleteLabel
                    :background="suggestion.meta?.background"
                    :text="suggestion.meta?.text"
                    role="listitem"
                  >
                    {{ suggestion.category }}
                  </AppAutocompleteLabel>
                </div>
                <mdicon
                  v-if="suggestionSelected(suggestion)"
                  class="ml-2 text-red-2"
                  size="20"
                  aria-hidden="true"
                  name="check"
                />
              </span>
            </slot>
          </li>
        </template>
      </ul>
    </Teleport>
    <AppErrorLabel v-if="error" :id="`${id}-error`" class="mt-1"
      >{{ error }}
    </AppErrorLabel>
    <AppHelpLabel v-if="helpLabel" :id="`${id}-help`" class="mt-1"
      >{{ helpLabel }}
    </AppHelpLabel>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { useAutocomplete } from '@/composables/useAutocomplete'
import { useDropdownPosition } from '@/composables/useDropdownPosition'
import { darkControlBgClass } from '../../../stores/themeStore.ts'
import AppAutocompleteLabel from './AppAutocompleteLabel.vue'

const props = defineProps({
  helpLabel: {
    type: String,
    default: '',
  },
  background: {
    type: String,
    default: 'grey-10',
  },
  // A single suggestion object, or an array of them when multiple is set
  modelValue: {
    type: [Object, Array],
    default: (rawProps) => (rawProps.multiple ? [] : null),
  },
  multiple: { type: Boolean, default: false },
  label: { type: String, default: '' },
  searchOnFocus: { type: Boolean, default: true },
  placeholder: { type: String, default: '' },
  error: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  id: {
    type: String,
    default: () => `autocomplete-${Math.random().toString(36).substr(2, 9)}`,
  },
  apiEndpoint: { type: String, required: true },
  labelKey: { type: String, default: 'label' },
  allowUnselect: { type: Boolean, default: true },
  valueKey: { type: String, default: 'value' },
  minChars: { type: Number, default: 2 },
})

const emit = defineEmits(['update:modelValue'])

const containerRef = ref(null)
const comboboxRef = ref(null)
const panelRef = ref(null)

const {
  searchTerm,
  suggestions,
  showSuggestions,
  apiError,
  isLoading,
  chevronIcon,
  closeSuggestions,
  toggleSuggestionsAndFocus,
  onInput,
  cleanup,
  inputRef,
} = useAutocomplete(props)

const { panelStyle } = useDropdownPosition({
  isOpen: showSuggestions,
  triggerRef: comboboxRef,
})

const selectedItems = computed(() =>
  props.multiple && Array.isArray(props.modelValue) ? props.modelValue : []
)

const hasSelectedValues = computed(() => selectedItems.value.length > 0)

const inputPlaceholder = computed(() =>
  hasSelectedValues.value ? '' : props.placeholder
)

// Loose comparison so numeric ids still match when one side is a string
const suggestionSelected = (suggestion) =>
  props.multiple
    ? selectedItems.value.some(
        (item) => item[props.valueKey] == suggestion[props.valueKey]
      )
    : props.modelValue &&
      suggestion[props.valueKey] == props.modelValue[props.valueKey]

// In multi mode selected items are shown as chips, so drop them from the list
const visibleSuggestions = computed(() =>
  props.multiple
    ? suggestions.value.filter((suggestion) => !suggestionSelected(suggestion))
    : suggestions.value
)

const selectSuggestion = (suggestion) => {
  if (props.multiple) {
    if (!suggestionSelected(suggestion)) {
      emit('update:modelValue', [...selectedItems.value, suggestion])
      searchTerm.value = ''
    }
  } else if (suggestionSelected(suggestion) && props.allowUnselect) {
    emit('update:modelValue', null)
    searchTerm.value = null
  } else {
    emit('update:modelValue', suggestion)
    searchTerm.value = suggestion[props.labelKey]
  }
  closeSuggestions()
  apiError.value = null
}

const removeItem = (item) => {
  emit(
    'update:modelValue',
    selectedItems.value.filter((i) => i[props.valueKey] != item[props.valueKey])
  )
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (props.multiple) {
      if (!hasSelectedValues.value) {
        suggestions.value = []
        closeSuggestions()
        apiError.value = null
      }
    } else if (!newValue) {
      suggestions.value = []
      closeSuggestions()
      apiError.value = null
      searchTerm.value = ''
    } else {
      searchTerm.value = newValue[props.labelKey]
    }
  },
  { immediate: true, deep: props.multiple }
)

const handleClickOutside = (event) => {
  // The listbox is teleported to <body>, so check it separately
  if (panelRef.value?.contains(event.target)) {
    return
  }
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    closeSuggestions()
    apiError.value = null
    searchTerm.value = props.multiple
      ? ''
      : props.modelValue?.[props.labelKey] || ''
  }
}

defineExpose({
  closeSuggestions,
  focus: () => inputRef.value?.focus(),
})
onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  cleanup()
})
</script>
